import axios from 'axios';
import { Timestamp } from 'firebase/firestore';
import { SeasonSummary, TmdbSummary } from './types';

const API = 'https://api.themoviedb.org/3';
const params = { api_key: process.env.REACT_APP_TMDB_API_KEY };

export const posterUrl = (path?: string | null, size = 'w185') =>
  path ? `https://image.tmdb.org/t/p/${size}${path}` : '';

export interface TmdbSearchResult {
  id: number;
  name: string;
  first_air_date?: string;
  poster_path?: string | null;
}

export const searchShows = async (query: string) => {
  const res = await axios.get(`${API}/search/tv`, {
    params: { ...params, query },
  });
  return res.data.results as TmdbSearchResult[];
};

// One request covers every season. Episodes out per season come from the last
// aired episode: earlier seasons are complete, later ones haven't started.
// Season 0 (specials) is left out.
export const fetchShowSummary = async (tmdbId: number): Promise<TmdbSummary> => {
  const { data } = await axios.get(`${API}/tv/${tmdbId}`, { params });
  const last = data.last_episode_to_air;

  const seasons: SeasonSummary[] = (data.seasons ?? [])
    .filter((s: any) => s.season_number > 0)
    .map((s: any) => {
      const episodeCount = s.episode_count ?? 0;
      let aired = 0;
      if (last) {
        if (s.season_number < last.season_number) aired = episodeCount;
        else if (s.season_number === last.season_number) {
          aired = Math.min(last.episode_number, episodeCount);
        }
      }
      return { n: s.season_number, name: s.name, episodeCount, aired };
    });

  return {
    status: data.status ?? '',
    lastAirDate: last?.air_date ?? null,
    nextAirDate: data.next_episode_to_air?.air_date ?? null,
    seasons,
    checkedAt: Timestamp.now(),
  };
};

export interface Episode {
  number: number;
  name: string;
  airDate: string | null;
}

// Episode names are only needed in the dialog, so they're kept in memory for
// the session rather than stored.
const seasonCache = new Map<string, Promise<Episode[]>>();

export const fetchSeasonEpisodes = (tmdbId: number, season: number) => {
  const key = `${tmdbId}/${season}`;
  let cached = seasonCache.get(key);
  if (!cached) {
    cached = axios
      .get(`${API}/tv/${tmdbId}/season/${season}`, { params })
      .then(({ data }) =>
        (data.episodes ?? []).map((e: any) => ({
          number: e.episode_number,
          name: e.name,
          airDate: e.air_date ?? null,
        }))
      );
    cached.catch(() => seasonCache.delete(key));
    seasonCache.set(key, cached);
  }
  return cached;
};

export const imdbUrl = async (tmdbId: number, title: string) => {
  try {
    const { data } = await axios.get(`${API}/tv/${tmdbId}/external_ids`, {
      params,
    });
    if (data.imdb_id) return `https://www.imdb.com/title/${data.imdb_id}/`;
  } catch {
    // Fall through to a search
  }
  return `https://www.imdb.com/find/?q=${encodeURIComponent(title)}&s=tt`;
};

// YouTube key of the show's trailer: an official Trailer first, then any
// Trailer, then a Teaser. Kept in memory for the session like seasons.
const trailerCache = new Map<number, Promise<string | null>>();

export const fetchTrailerKey = (tmdbId: number) => {
  let cached = trailerCache.get(tmdbId);
  if (!cached) {
    cached = axios
      .get(`${API}/tv/${tmdbId}/videos`, {
        params: { ...params, include_video_language: 'en,null' },
      })
      .then(({ data }) => {
        const videos = (data.results ?? []).filter(
          (v: any) => v.site === 'YouTube'
        );
        const pick =
          videos.find((v: any) => v.type === 'Trailer' && v.official) ??
          videos.find((v: any) => v.type === 'Trailer') ??
          videos.find((v: any) => v.type === 'Teaser');
        return (pick?.key as string) ?? null;
      });
    cached.catch(() => trailerCache.delete(tmdbId));
    trailerCache.set(tmdbId, cached);
  }
  return cached;
};
